import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stepper, Step, StepLabel, CircularProgress, Typography, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import { useLocation, useNavigate } from 'react-router-dom';

import { setEmail, setfirstName, setlastName, setEntityId, setwizardPageNumber, setWizardStepValidation, setInvitationId, setEntityName, resetAllFields } from '../../authslice';
import WizardFormFirstPage from './RegisterPersonalAccount';
import WizardFormSecondPage from './SetPassword';
import WizardFormThirdPage from './EmailVerfication';
import WizardFormFourthPage from './WelcomeScreen';
import { RootState, AppDispatch } from '../../store';
import Header from 'MyComponents/Includes/Header';
import { validateInvite } from "Utils/inivite";
import { validateEntityId } from "Utils/entity";
import useNetworkStatus from 'hooks/useNetworkStatus';
import OfflineBanner from 'MyComponents/UI/OfflineBanner';
import Footer from 'MyComponents/Includes/Footer';

const defaultSteps = ['Personal', 'Password', 'Verify', 'Welcome'];

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#e0e0e0',
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const CustomStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: ownerState.active || ownerState.completed ? '#00D084' : '#A9B5C1',
    zIndex: 1,
    color: '#fff',
    width: 24,
    height: 24,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    ...(ownerState.error && {
      backgroundColor: '#f44336',
    }),
    [theme.breakpoints.up('md')]: {
      ...( (ownerState.active || ownerState.completed) && {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: '#fff',
      }),
      ...( ownerState.error && {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: '#fff',
      }),
    },
  }),
);

function CustomStepIcon(props: StepIconProps) {
  const { active, completed, error, className, icon } = props;

  return (
    <CustomStepIconRoot ownerState={{ completed, active, error }} className={className}>
      {completed ? '✓' : String(icon)}
    </CustomStepIconRoot>
  );
}

const CustomStepperContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 1000,
  margin: '0 auto',
  padding: '24px 20px 16px 20px',
  [theme.breakpoints.down('md')]: {
    position: 'sticky',
    top: 72,
    zIndex: 1010,
    backgroundColor: 'var(--bs-body-bg, #fff)',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    paddingTop: '20px',
    paddingBottom: '8px',
  }
}));

const WizardPersonalAccount = () => {
  const dispatch = useDispatch<AppDispatch>();
  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);
  const themeObj = useTheme();
  const isMobile = useMediaQuery(themeObj.breakpoints.down('md'));
  
  const desktopSteps = ['Personal Information', 'Set Password', 'Verify Email', 'Welcome'];
  const steps = isMobile ? defaultSteps : desktopSteps;
  const stepValidation = useSelector((state: RootState) => state.auth.wizardStepValidation);
  const { isOnline } = useNetworkStatus();

  const persistedEntityId = useSelector((state: RootState) => state.auth.entityId);
  const persistedInvitationId = useSelector((state: RootState) => state.auth.invitationId);

  const isStepFailed = (step: number) => !stepValidation[step];

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const invitationId = queryParams.get('invitationId') || persistedInvitationId || null;
  const entityId = queryParams.get('entityId') || persistedEntityId || null;

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (page > 1 && page < 4) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [page]);

  const [loadingInvite, setLoadingInvite] = React.useState<boolean>(!!invitationId);
  const [inviteError, setInviteError] = React.useState<string | null>(null);

  const [loadingEntity, setLoadingEntity] = React.useState<boolean>(!!entityId && !invitationId);
  const [entityError, setEntityError] = React.useState<string | null>(null);

  // If the URL points at a different entity/invitation than what's persisted from a
  // previous same-tab session, wipe the stale wizard state before adopting the new one.
  React.useEffect(() => {
    const urlEntityIdParam = queryParams.get('entityId') || '';
    const urlInvitationIdParam = queryParams.get('invitationId') || '';

    let identifierChanged = false;
    if (urlInvitationIdParam) {
      // Invite flow: entityId is derived from the invite, so invitationId is the real identifier.
      identifierChanged = urlInvitationIdParam !== persistedInvitationId;
    } else if (urlEntityIdParam) {
      // Direct entity flow: also reset if we were previously in an invite-bound session.
      identifierChanged = urlEntityIdParam !== persistedEntityId || !!persistedInvitationId;
    }

    if (identifierChanged) {
      dispatch(resetAllFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const handleEntity = async () => {
      if (!entityId) return;

      setLoadingEntity(true);
      const response = await validateEntityId(entityId);

      if (response.data) {
        dispatch(setEntityId(entityId));
        if (response.data.displayName) {
          dispatch(setEntityName(response.data.displayName));
        }

        if (!response.data.isApproved) {
          navigate('/onboarding/entity-pending', { replace: true });
          return;
        }

        setEntityError(null);
      } else {
        setEntityError(response.error || "Invalid entity ID or entity not found.");
      }

      setLoadingEntity(false);
    };

    handleEntity();
  }, [entityId]);

  React.useEffect(() => {
    const handleInvite = async () => {
      if (!invitationId) return;

      const { data, error } = await validateInvite(invitationId, false);

      if (data) {
        dispatch(setfirstName(data.firstName));
        dispatch(setlastName(data.lastName));
        dispatch(setEmail(data.email));
        dispatch(setEntityId(data.entityId));
        dispatch(setInvitationId(invitationId));
        setInviteError(null);
      } else {
        setInviteError(error);
      }

      setLoadingInvite(false);
    };

    handleInvite();
  }, [invitationId]);

  if (loadingInvite || loadingEntity) {
    return (
      <div style={{ marginTop: 100, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          {loadingInvite ? "Validating your invitation..." : "Validating entity..."}
        </Typography>
      </div>
    );
  }

  if (inviteError || entityError) {
    return (
      <div style={{ marginTop: 100, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {inviteError || entityError}
        </Typography>
        <Typography variant="body2">
          {inviteError
            ? "Please check your invitation link or contact ARMF support."
            : "Please check your entity link or contact ARMF support."}
        </Typography>
      </div>
    );
  }

  return (
    <>
      <OfflineBanner isOnline={isOnline} />
      <Header />
      <CustomStepperContainer>
        <Stepper activeStep={page - 1} alternativeLabel={isMobile} connector={<CustomConnector />}>
          {steps.map((label, index) => {
            const hasError = isStepFailed(index + 1);
            const isActiveOrCompleted = index <= page - 1;
            return (
            <Step key={label}>
              <StepLabel
                error={hasError}
                slots={{ stepIcon: CustomStepIcon }}
                sx={{
                  ...(isMobile ? {
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      color: '#A9B5C1',
                      marginTop: '4px',
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      color: '#00D084',
                      fontWeight: 600,
                    },
                    '& .MuiStepLabel-label.Mui-completed': {
                      color: '#707070',
                      fontWeight: 500,
                    },
                    '& .MuiStepLabel-label.Mui-error': {
                      color: '#f44336',
                    },
                  } : {
                    backgroundColor: hasError ? '#f44336' : (isActiveOrCompleted ? '#00D084' : '#f0f0f0'),
                    padding: '6px 20px 6px 6px',
                    borderRadius: '50px',
                    '& .MuiStepLabel-label': {
                      fontSize: '0.85rem',
                      color: isActiveOrCompleted ? '#fff !important' : '#707070',
                      fontWeight: 600,
                      marginLeft: '2px',
                    }
                  })
                }}
              >
                {label}
              </StepLabel>
            </Step>
            );
          })}
        </Stepper>
      </CustomStepperContainer>

      <div className="d-flex flex-column flex-grow-1 mx-auto w-100" style={{ maxWidth: '1920px' }}>
        {page === 1 && <WizardFormFirstPage />}
        {page === 2 && <WizardFormSecondPage />}
        {page === 3 && <WizardFormThirdPage />}
        {page === 4 && <WizardFormFourthPage />}
      </div>
      <Footer />
    </>
  );
};

export default WizardPersonalAccount;
